/**
 * sessionStorage 기반 영구 상태 hook.
 *
 *   const [q, setQ] = usePersistedState('purchase-list/q', '');
 *
 * - 같은 세션 (브라우저 탭 살아 있는 동안) 메뉴 이동 후 돌아와도 값 유지
 * - 탭 닫으면 사라짐 (localStorage 와 다르게 영구 X)
 * - key 충돌 방지: 호출 측에서 prefix 권장 (예: 'purchase-list/q')
 * - 직렬화: JSON. Set/Map 등 비-JSON 값은 호출 측에서 변환해서 전달.
 */

import { useCallback, useState } from 'react';

export function usePersistedState<T>(
  key: string,
  initial: T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const [value, setValueRaw] = useState<T>(() => {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw === null) return initial;
      return JSON.parse(raw) as T;
    } catch {
      return initial;
    }
  });

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValueRaw((prev) => {
        const resolved =
          typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        try {
          sessionStorage.setItem(key, JSON.stringify(resolved));
        } catch {
          /* QuotaExceeded 등 — 메모리만 갱신 */
        }
        return resolved;
      });
    },
    [key],
  );

  return [value, setValue];
}
